import React, { createContext, Dispatch, FC, useContext, useEffect, useReducer, useState } from 'react'
import config from '../../config'

type RecaptchaState = {
  recaptcha: any | undefined
}

enum RecaptchaActionTypes {
  SET = '[recaptcha] SET',
}

type SetRecaptchaAction = {
  type: RecaptchaActionTypes.SET
  payload: any
}

// This type is for consistency with other Provider implementations
type RecaptchaActions = SetRecaptchaAction

const RecaptchaStateContext = createContext<RecaptchaState>({} as RecaptchaState)
const RecaptchaDispatchContext = createContext<Dispatch<RecaptchaActions>>(() => void 0)

const reducer = (state: RecaptchaState, action: RecaptchaActions) => {
  switch (action.type) {
    case RecaptchaActionTypes.SET:
      return {
        ...state,
        recaptcha: action.payload,
      }
    default:
      return state
  }
}

// Load reCAPTCHA enterprise for all pages
const RecaptchaProvider: FC = ({ children }) => {
  const initialState = { recaptcha: undefined }
  const [state, dispatch] = useReducer(reducer, initialState)

  useEffect(() => {
    if (window.grecaptcha) return
    const script = document.createElement('script')
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${config.recaptchaKey}`
    script.async = true
    script.addEventListener('load', () => {
      dispatch({ type: RecaptchaActionTypes.SET, payload: window.grecaptcha })
    })
    document.head.appendChild(script)
    return () => {
      document.head.removeChild(script)
    }
  }, [])

  return (
    <RecaptchaStateContext.Provider value={state}>
      <RecaptchaDispatchContext.Provider value={dispatch}>{children}</RecaptchaDispatchContext.Provider>
    </RecaptchaStateContext.Provider>
  )
}

function useRecaptchaState() {
  const context = useContext(RecaptchaStateContext)
  if (context === undefined) {
    throw new Error('useRecaptchaState must be provided with a RecaptchaProvider')
  }
  return context
}

function useRecaptchaDispatch() {
  const context = useContext(RecaptchaDispatchContext)
  if (context === undefined) {
    throw new Error('useRecaptchaDispatch must be provided with a RecaptchaProvider')
  }
  return context
}

function useRecaptcha() {
  const state = useRecaptchaState()
  return state.recaptcha
}

export { RecaptchaProvider, useRecaptchaState, useRecaptchaDispatch, useRecaptcha }
